export interface UserProfile {
  full_name: string;
  email: string;
  phone: string;
  company: string;
  title: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  zip: string;
  organization_name: string;
  organization_tagline: string;
  organization_logo_data_url: string;
}

export interface UserSignature {
  body: string;
  include_phone: boolean;
  include_company: boolean;
}

export interface PaymentMethod {
  brand: 'visa' | 'mastercard' | 'amex' | 'discover' | '';
  last4: string;
  exp_month: string;
  exp_year: string;
  cardholder: string;
  billing_zip: string;
}

export interface UserSettings {
  profile: UserProfile;
  signature: UserSignature;
  payment: PaymentMethod;
}

const STORAGE_KEY = 'rlpscout:user-settings:v1';

export const DEFAULT_SETTINGS: UserSettings = {
  profile: {
    full_name: 'George Mogga',
    email: 'georgemogga1@gmail.com',
    phone: '',
    company: 'University of Utah',
    title: 'Operator',
    address_line1: '',
    address_line2: '',
    city: 'Salt Lake City',
    state: 'UT',
    zip: '',
    organization_name: 'University of Utah',
    organization_tagline: 'University Hospital · UT',
    organization_logo_data_url: '',
  },
  signature: {
    body:
      'George Mogga\nOccasion Crafters LLC · RLPScout\ngeorgemogga1@gmail.com',
    include_phone: true,
    include_company: true,
  },
  payment: {
    brand: '',
    last4: '',
    exp_month: '',
    exp_year: '',
    cardholder: '',
    billing_zip: '',
  },
};

// Cache the parsed snapshot keyed by raw storage string. React's
// useSyncExternalStore requires getSnapshot to return a stable reference
// when the underlying data is unchanged — without this cache we'd build a
// new object every render and trip the "Maximum update depth" guard.
let cachedRaw: string | null = null;
let cachedSnapshot: UserSettings = DEFAULT_SETTINGS;

export function loadSettings(): UserSettings {
  if (typeof window === 'undefined') return DEFAULT_SETTINGS;
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return DEFAULT_SETTINGS;
  }
  if (raw === cachedRaw) return cachedSnapshot;
  cachedRaw = raw;
  if (!raw) {
    cachedSnapshot = DEFAULT_SETTINGS;
    return cachedSnapshot;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<UserSettings> & { payment?: unknown };
    // One-shot purge: if a prior build wrote payment data, strip it now.
    if (parsed.payment != null) {
      const { payment: _drop, ...clean } = parsed;
      const cleanedRaw = JSON.stringify(clean);
      window.localStorage.setItem(STORAGE_KEY, cleanedRaw);
      cachedRaw = cleanedRaw;
    }
    cachedSnapshot = {
      profile: { ...DEFAULT_SETTINGS.profile, ...(parsed.profile ?? {}) },
      signature: { ...DEFAULT_SETTINGS.signature, ...(parsed.signature ?? {}) },
      // Payment fields are deliberately NOT read from localStorage: cardholder
      // name / exp / billing_zip are PCI-sensitive and must not persist to
      // disk. Until Stripe Elements is wired up, this stays session-only.
      payment: { ...DEFAULT_SETTINGS.payment },
    };
    return cachedSnapshot;
  } catch {
    cachedSnapshot = DEFAULT_SETTINGS;
    return cachedSnapshot;
  }
}

const SETTINGS_EVENT = 'rlpscout:settings-changed';

export function saveSettings(next: UserSettings): void {
  if (typeof window === 'undefined') return;
  // Strip payment before persisting — see loadSettings() for the reason.
  const { payment: _payment, ...persistable } = next;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(persistable));
  window.dispatchEvent(new CustomEvent(SETTINGS_EVENT));
}

export function clearSettings(): void {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new CustomEvent(SETTINGS_EVENT));
}

export function subscribeSettings(listener: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const onChange = () => listener();
  window.addEventListener(SETTINGS_EVENT, onChange);
  window.addEventListener('storage', onChange);
  return () => {
    window.removeEventListener(SETTINGS_EVENT, onChange);
    window.removeEventListener('storage', onChange);
  };
}
